// FILE: src/components/Admin/AI/kb/PipelineFilesTable.tsx
import React from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';

import { useDriftDeletePipelineFileMutation } from '../../../../hooks/ai-hooks';
import { DriftPipelineFile } from '../../../../types/aiTypes';

type Props = {
  pipelineId: string;
  files: DriftPipelineFile[];
  onDeleted: () => void;
  disabled?: boolean;
};

type SortDirection = 'asc' | 'desc';

type SortField =
  | 'filename'
  | 'size'
  | 'content_type'
  | 'updated_at';

type FileRow = DriftPipelineFile & {
  filename?: string;
  path?: string;
  size?: number;
  type?: string;
  content_type?: string;
  updated_at?: string;
  last_modified?: string;
};

function sizeLabel(size?: number) {
  if (size === undefined || size === null || Number.isNaN(Number(size))) return '-';

  const numeric = Number(size);

  if (numeric < 1024) return `${numeric} B`;
  if (numeric < 1024 * 1024) return `${Math.round(numeric / 1024)} KB`;

  return `${Math.round(numeric / (1024 * 1024))} MB`;
}

function normalizeString(value: any) {
  return String(value ?? '').trim();
}

function getFileName(file: FileRow, idx?: number) {
  return normalizeString(file.filename || file.path || `file-${idx ?? ''}`) || `file-${idx ?? ''}`;
}

function getFileContentType(file: FileRow) {
  return normalizeString(file.content_type || file.type || '');
}

function getFileUpdatedAt(file: FileRow) {
  return normalizeString(file.updated_at || file.last_modified || (file as any).last_updated || '');
}

function getFileExtension(file: FileRow, idx?: number) {
  const filename = getFileName(file, idx);
  const clean = filename.split('?')[0].split('#')[0];
  const parts = clean.split('.');

  if (parts.length < 2) return '';

  return parts[parts.length - 1].toLowerCase();
}

function dateLabel(value?: string | null) {
  if (!value) return '-';

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  } catch {
    return String(value);
  }
}

function compareValues(a: any, b: any, direction: SortDirection) {
  let result = 0;

  if (typeof a === 'number' || typeof b === 'number') {
    result = Number(a || 0) - Number(b || 0);
  } else {
    result = String(a || '').localeCompare(String(b || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  return direction === 'desc' ? -result : result;
}

function getSortValue(file: FileRow, field: SortField, idx: number) {
  if (field === 'filename') return getFileName(file, idx);
  if (field === 'size') return Number(file.size || 0);
  if (field === 'content_type') return getFileContentType(file);
  if (field === 'updated_at') {
    const raw = getFileUpdatedAt(file);
    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
  }

  return '';
}

const PipelineFilesTable: React.FC<Props> = ({
                                               pipelineId,
                                               files,
                                               onDeleted,
                                               disabled,
                                             }) => {
  const deleteMutation = useDriftDeletePipelineFileMutation();

  const [search, setSearch] = React.useState('');
  const [contentTypeFilter, setContentTypeFilter] = React.useState('');
  const [extensionFilter, setExtensionFilter] = React.useState('');
  const [minSize, setMinSize] = React.useState('');
  const [maxSize, setMaxSize] = React.useState('');
  const [sortField, setSortField] = React.useState<SortField>('filename');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  React.useEffect(() => {
    setPage(0);
  }, [search, contentTypeFilter, extensionFilter, minSize, maxSize, files.length]);

  const normalizedFiles = React.useMemo(
    () =>
      (files || []).map((file, idx) => ({
        ...file,
        filename: getFileName(file as FileRow, idx),
        size: Number((file as FileRow).size || 0),
        content_type: getFileContentType(file as FileRow),
        updated_at: getFileUpdatedAt(file as FileRow),
      })),
    [files],
  );

  const contentTypeOptions = React.useMemo(() => {
    const values = new Set<string>();

    normalizedFiles.forEach((file) => {
      const value = getFileContentType(file);
      if (value) values.add(value);
    });

    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [normalizedFiles]);

  const extensionOptions = React.useMemo(() => {
    const values = new Set<string>();

    normalizedFiles.forEach((file, idx) => {
      const value = getFileExtension(file, idx);
      if (value) values.add(value);
    });

    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [normalizedFiles]);

  const filteredFiles = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const min = minSize.trim() === '' ? null : Number(minSize);
    const max = maxSize.trim() === '' ? null : Number(maxSize);

    return normalizedFiles.filter((file, idx) => {
      const filename = getFileName(file, idx);
      const contentType = getFileContentType(file);
      const updatedAt = getFileUpdatedAt(file);
      const extension = getFileExtension(file, idx);
      const size = Number(file.size || 0);

      if (q) {
        const haystack = [
          filename,
          contentType,
          updatedAt,
          extension,
          sizeLabel(size),
        ]
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(q)) return false;
      }

      if (contentTypeFilter && contentType !== contentTypeFilter) return false;
      if (extensionFilter && extension !== extensionFilter) return false;
      if (min !== null && Number.isFinite(min) && size < min) return false;
      if (max !== null && Number.isFinite(max) && size > max) return false;

      return true;
    });
  }, [normalizedFiles, search, contentTypeFilter, extensionFilter, minSize, maxSize]);

  const sortedFiles = React.useMemo(() => {
    return [...filteredFiles].sort((a, b) => {
      const aIdx = normalizedFiles.indexOf(a);
      const bIdx = normalizedFiles.indexOf(b);

      return compareValues(
        getSortValue(a, sortField, aIdx),
        getSortValue(b, sortField, bIdx),
        sortDirection,
      );
    });
  }, [filteredFiles, normalizedFiles, sortDirection, sortField]);

  const pagedFiles = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sortedFiles.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedFiles]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }

    setPage(0);
  };

  const removeFile = async (file: FileRow) => {
    const filename = file.filename || file.path;
    if (!filename || !pipelineId) return;

    if (!window.confirm(`Remove "${filename}" from the pipeline resources?`)) return;

    await deleteMutation.mutateAsync({
      pipeline_id: pipelineId,
      filename,
      delete_vectors: true,
    });

    onDeleted();
  };

  const clearFilters = () => {
    setSearch('');
    setContentTypeFilter('');
    setExtensionFilter('');
    setMinSize('');
    setMaxSize('');
    setPage(0);
  };

  const renderSortLabel = (field: SortField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDirection : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  const hasActiveFilters =
    Boolean(search.trim()) ||
    Boolean(contentTypeFilter) ||
    Boolean(extensionFilter) ||
    Boolean(minSize.trim()) ||
    Boolean(maxSize.trim());

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        mb={2}
      >
        <Box>
          <Typography variant="h6">Pipeline Files</Typography>
          <Typography variant="body2" color="text.secondary">
            Documents currently returned by the selected pipeline metadata.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" variant="outlined" label={`${filteredFiles.length} shown`} />
          <Chip size="small" variant="outlined" label={`${normalizedFiles.length} total`} />
        </Stack>
      </Stack>

      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Search files"
            placeholder="Search filename, content type, date, or size..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            select
            size="small"
            label="Content Type"
            value={contentTypeFilter}
            onChange={(event) => setContentTypeFilter(event.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {contentTypeOptions.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            select
            size="small"
            label="Extension"
            value={extensionFilter}
            onChange={(event) => setExtensionFilter(event.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {extensionOptions.map((value) => (
              <MenuItem key={value} value={value}>
                .{value}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6} md={1.5}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Min bytes"
            value={minSize}
            onChange={(event) => setMinSize(event.target.value)}
          />
        </Grid>

        <Grid item xs={6} md={1.5}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Max bytes"
            value={maxSize}
            onChange={(event) => setMaxSize(event.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={1}>
          <Button
            fullWidth
            variant="outlined"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            sx={{ height: '100%' }}
          >
            Clear
          </Button>
        </Grid>
      </Grid>

      {sortedFiles.length ? (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{renderSortLabel('filename', 'File')}</TableCell>
                  <TableCell width={180}>{renderSortLabel('content_type', 'Content Type')}</TableCell>
                  <TableCell width={120}>{renderSortLabel('size', 'Size')}</TableCell>
                  <TableCell width={190}>{renderSortLabel('updated_at', 'Updated')}</TableCell>
                  <TableCell width={90} align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pagedFiles.map((file, idx) => {
                  const absoluteIndex = page * rowsPerPage + idx;
                  const filename = getFileName(file, absoluteIndex);
                  const contentType = getFileContentType(file);

                  return (
                    <TableRow key={`${filename}-${absoluteIndex}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                          {filename}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {contentType ? (
                          <Chip size="small" variant="outlined" label={contentType} />
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      <TableCell>{sizeLabel(file.size)}</TableCell>

                      <TableCell>{dateLabel(getFileUpdatedAt(file))}</TableCell>

                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          disabled={disabled || deleteMutation.isPending || !pipelineId}
                          onClick={() => removeFile(file)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <TablePagination
            component="div"
            count={sortedFiles.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {normalizedFiles.length
            ? 'No files match the current search or filters.'
            : 'No files returned, or the selected pipeline endpoint does not expose a file list.'}
        </Typography>
      )}
    </Paper>
  );
};

export default PipelineFilesTable;